// src/pages/MyPayoutsPage.js
import React, { useEffect, useState } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'

// 1) Import your custom axios instance (with interceptors) instead of plain axios
import axiosInstance from '../utils/axiosInstance'

function MyPayoutsPage() {
  const { lang } = useOutletContext() || {}
  const navigate = useNavigate()

  // We'll store the schedule info
  const [interval, setIntervalVal] = useState('')
  const [anchor, setAnchor] = useState('')
  const [balance, setBalance] = useState(0)
  const [expectedDate, setExpectedDate] = useState(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Grab token from localStorage (for auth)
  const token = localStorage.getItem('token')
  // Grab user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  // Basic translations for this page
  const translations = {
    en: {
      pageTitle: 'My Payouts',
      payoutSchedule: 'Payout Schedule',
      paymentInterval: 'Interval',
      paymentDay: 'Payment Day',
      payoutBalance: 'Payout Balance',
      expectedDateLabel: 'Max Payout Date',
      loading: 'Loading...',
      error: 'An error occurred while fetching payout information.',
      pleaseLogin: 'Please log in first.',
      noUpcoming: 'No upcoming payout scheduled.',

      // New alert text
      freeAccountAlert: 'Payouts for free accounts are made once a week. Premium accounts with shorter payout times will be available soon.'
    },
    ja: {
      pageTitle: 'マイペイアウト',
      payoutSchedule: '支払いスケジュール',
      paymentInterval: '支払間隔',
      paymentDay: '支払日',
      payoutBalance: '支払可能残高',
      expectedDateLabel: '最大ペイアウト日',
      loading: '読み込み中...',
      error: 'ペイアウト情報の取得中にエラーが発生しました。',
      pleaseLogin: '最初にログインしてください。',
      noUpcoming: '次のペイアウトは予定されていません。',

      // New alert text (Japanese)
      freeAccountAlert: '無料アカウントの支払いは週に一度行われます。短い支払い期間のプレミアムアカウントは、まもなく利用可能になります。'
    },
    pt: {
      pageTitle: 'Meus Pagamentos',
      payoutSchedule: 'Agenda de Pagamentos',
      paymentInterval: 'Intervalo',
      paymentDay: 'Dia de Pagamento',
      payoutBalance: 'Saldo para Pagamento',
      expectedDateLabel: 'Data Máxima de Pagamento',
      loading: 'Carregando...',
      error: 'Ocorreu um erro ao buscar as informações de pagamento.',
      pleaseLogin: 'Por favor, faça login primeiro.',
      noUpcoming: 'Nenhum pagamento futuro agendado.',

      // New alert text (Portuguese)
      freeAccountAlert: 'Os pagamentos para contas gratuitas são feitos uma vez por semana. Contas premium com um tempo de pagamento mais curto estarão disponíveis em breve.'
    },
  }

  const t = translations[lang] || translations.en

  // Helper function to parse & store the data
  function handlePayoutData(data) {
    // e.g. { interval: "weekly", weekly_anchor: "friday", balance: 1200, expectedPayoutDate: ... }
    let capitalizedInterval = ''
    if (data.interval) {
      capitalizedInterval =
        data.interval.charAt(0).toUpperCase() + data.interval.slice(1)
    }

    let capitalizedAnchor = ''
    if (data.weekly_anchor) {
      capitalizedAnchor =
        data.weekly_anchor.charAt(0).toUpperCase() +
        data.weekly_anchor.slice(1)
    }

    setIntervalVal(capitalizedInterval)
    setAnchor(capitalizedAnchor)
    setBalance(data.balance || 0)

    if (data.expectedPayoutDate) {
      const dt = new Date(data.expectedPayoutDate * 1000)
      const options = { month: 'short', day: 'numeric' }
      const formattedDate = dt.toLocaleDateString('en-US', options)
      setExpectedDate(formattedDate)
    } else {
      setExpectedDate(null)
    }
  }

  // Main fetch function => uses our axiosInstance
  async function fetchPayoutData() {
    if (!token || !user.id) {
      setLoading(false)
      setError(t.pleaseLogin)
      return
    }

    setLoading(true)
    setError('')

    try {
      // Use axiosInstance (it will handle token & possible refresh)
      const res = await axiosInstance.get('/api/connect/my-payouts')
      handlePayoutData(res.data)
    } catch (err) {
      console.error('Error fetching payout data:', err)
      setError(t.error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (token) {
      fetchPayoutData()
    }
    // eslint-disable-next-line
  }, [token, user.id, t.error, t.pleaseLogin])

  // If no user, show a "Please log in" message
  if (!token || !user.id) {
    return <div className="max-w-xl mx-auto p-4 text-red-600">{t.pleaseLogin}</div>
  }

  return (
    // Increase container width on desktop => e.g. max-w-4xl, some padding
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h2 className="text-2xl font-semibold mb-6">{t.pageTitle}</h2>

      {/* New Alert (placed between the title and the boxes) */}
      <div className="flex gap-2 px-4 py-3 text-sm border rounded-md md:items-center border-custom-200 text-custom-500 bg-custom-50 dark:bg-custom-400/20 dark:border-custom-500/50 mb-6">
        <img src="/payme-logo.png" alt="" className="rounded-full size-8" />
        <span>{t.freeAccountAlert}</span>
      </div>

      {loading && <p className="text-gray-500">{t.loading}</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* If no error & not loading => Show boxes with styling similar to the Dashboard colored boxes */}
      {!loading && !error && (
        // On larger screens, show 4 columns => "lg:grid-cols-4"
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Box 1: Interval */}
          <div className="relative overflow-hidden bg-green-100 dark:bg-green-500/20 
                          border-green-500/20 card rounded-md shadow-sm p-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 size-32 
                         text-green-200/50 dark:text-green-500/20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
            <div className="flex items-center justify-center w-12 h-12 
                            bg-green-500 rounded-md text-sm text-green-50 
                            relative z-10"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20" height="20" fill="none"
                stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className="lucide lucide-clock"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <h5 className="mt-3 mb-1 text-lg font-semibold text-gray-800 
                           dark:text-slate-200 relative z-10"
            >
              {t.paymentInterval}
            </h5>
            <p className="text-base text-gray-700 dark:text-slate-100 relative z-10">
              {interval || '—'}
            </p>
          </div>

          {/* Box 2: Payment Day */}
          <div className="relative overflow-hidden bg-orange-100 dark:bg-orange-500/20 
                          border-orange-500/20 card rounded-md shadow-sm p-4"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 
                         size-32 text-orange-200/50 dark:text-orange-500/20"
              fill="none"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M19 12H5"></path>
              <path d="M12 5l-7 7 7 7"></path>
            </svg>
            <div className="flex items-center justify-center w-12 h-12 bg-orange-500 
                            rounded-md text-sm text-orange-50 relative z-10"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20" height="20" fill="none"
                stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className="lucide lucide-calendar"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <h5 className="mt-3 mb-1 text-lg font-semibold text-gray-800 
                           dark:text-slate-200 relative z-10"
            >
              {t.paymentDay}
            </h5>
            <p className="text-base text-gray-700 dark:text-slate-100 relative z-10">
              {anchor || '—'}
            </p>
          </div>

          {/* Box 3: Payout Balance */}
          <div className="relative overflow-hidden bg-sky-100 dark:bg-sky-500/20 
                          border-sky-500/20 card rounded-md shadow-sm p-4"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 
                         size-32 text-sky-200/50 dark:text-sky-500/20"
              fill="none"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M3 6h18"></path>
              <path d="M7 12h10"></path>
              <path d="M10 18h4"></path>
            </svg>
            <div className="flex items-center justify-center w-12 h-12 bg-sky-500 
                            rounded-md text-sm text-sky-50 relative z-10"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20" height="20" fill="none"
                stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className="lucide lucide-wallet"
              >
                <path d="M22 12v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8Z"></path>
                <path d="M2 10V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4"></path>
                <path d="M18 12a2 2 0 1 1 0 4"></path>
              </svg>
            </div>
            <h5 className="mt-3 mb-1 text-lg font-semibold text-gray-800 
                           dark:text-slate-200 relative z-10"
            >
              {t.payoutBalance}
            </h5>
            <p className="text-base text-gray-700 dark:text-slate-100 relative z-10">
              {balance} ¥
            </p>
          </div>

          {/* Box 4: "Max Payout Date" => uses translation t.expectedDateLabel */}
          <div className="relative overflow-hidden bg-purple-100 dark:bg-purple-500/20 
                          border-purple-500/20 card rounded-md shadow-sm p-4"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 size-32 
                         text-purple-200/50 dark:text-purple-500/20"
              fill="none"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v14"></path>
              <path d="M18 2v8a2 2 0 0 1-2 2H2"></path>
              <path d="m2 2 20 20"></path>
            </svg>
            <div className="flex items-center justify-center w-12 h-12 bg-purple-500 
                            rounded-md text-sm text-purple-50 relative z-10"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20" height="20"
                fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className="lucide lucide-bell"
              >
                <path d="M18 8a6 6 0 0 0-9.33-5"></path>
                <path d="M13.29 20a2 2 0 0 1-2.58 0"></path>
                <path d="M2 13h20"></path>
                <path d="M8 8v2a4 4 0 0 0 8 0V8"></path>
                <path d="M8 14v2a4 4 0 0 0 8 0v-2"></path>
              </svg>
            </div>
            <h5 className="mt-3 mb-1 text-lg font-semibold text-gray-800 
                           dark:text-slate-200 relative z-10"
            >
              {t.expectedDateLabel}
            </h5>
            {expectedDate ? (
              <p className="text-base text-gray-700 dark:text-slate-100 relative z-10">
                {expectedDate}
              </p>
            ) : (
              <p className="text-sm text-gray-600 dark:text-slate-200 relative z-10">
                {t.noUpcoming}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MyPayoutsPage
